import { useTheme } from '@emotion/react';
import cn from 'classnames';
import { Children, memo, type PropsWithChildren, useMemo } from 'react';

import * as styles from './Stack.styles';

export type StackProps = {
  maxItemsToRender?: number;
  currentItemIndex?: number;
  withShadowEffect?: boolean;
  gapBetweenItems?: number;
} & PropsWithChildren;

export const StackBase = ({
  maxItemsToRender = 2,
  currentItemIndex = 0,
  withShadowEffect = true,
  gapBetweenItems = 5,
  children,
}: StackProps) => {
  const theme = useTheme();

  const childrenToArray = useMemo(
    () => Children.toArray(children).map((child) => child),
    [children],
  );

  return (
    <div css={styles.stackContainer}>
      {childrenToArray.map((child, index) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: stack position is the identity of each layer item
          key={index}
          css={styles.itemContainer(
            theme,
            index - currentItemIndex,
            childrenToArray.length - currentItemIndex,
            gapBetweenItems,
          )}
          className={cn({
            withShadowEffect,
            isFirstItem: index - currentItemIndex === 0,
            isHidden: index < currentItemIndex || index - currentItemIndex > maxItemsToRender,
          })}
        >
          {child}
        </div>
      ))}
    </div>
  );
};

StackBase.displayName = 'Stack';

export const Stack = memo(StackBase);
